import { logUsage } from 'owa-analytics';
import { debugLog } from '../logging/debugLog';
import { mockMRIs } from '../mock/mockMRIs';
import { shouldMockRequest } from '../mock/shouldMockRequest';
import { buildMriFromAADObjectId } from '../utils/buildMriFromAADObjectId';
import { getMRIsFromGraphInBulk } from './getMRIsFromGraphInBulk';

import type { GraphUsersResponse } from '../types/GraphUsersResponse';
import type { PresenceIdentifier } from '../types/PresenceIdentifier';
const userMRICache = new Map<string, PresenceIdentifier>();

export async function fetchMRIsFromMsGraph(
    smtps: string[],
    useInPlaceCache?: boolean
): Promise<PresenceIdentifier[]> {
    const resultReturned: PresenceIdentifier[] = [];
    const collaboratorMRIListToFetch: string[] = [];

    if (useInPlaceCache) {
        // get the MRIs from the cache
        smtps.forEach(email => {
            const mri = userMRICache.get(email);
            if (mri) {
                resultReturned.push(mri);
            } else {
                collaboratorMRIListToFetch.push(email);
            }
        });
    } else {
        collaboratorMRIListToFetch.push(...smtps);
    }

    // if the list to fetch is not empty, fetch the MRIs from the graph
    if (collaboratorMRIListToFetch.length > 0) {
        const collaboratorsMRIs = await fetchMRIs(collaboratorMRIListToFetch);
        collaboratorsMRIs.forEach((presenceIdentifier: GraphUsersResponse) => {
            const userSMTP = presenceIdentifier.mail;
            const userId = presenceIdentifier.id;
            if (!!userId) {
                const userPresenceId = {
                    mri: buildMriFromAADObjectId(presenceIdentifier.id),
                    smtp: userSMTP.toLocaleLowerCase(),
                };
                resultReturned.push(userPresenceId);
                useInPlaceCache && userMRICache.set(userSMTP.toLocaleLowerCase(), userPresenceId);
            }
        });
    }

    debugLog('fetchMRIsFromMsGraph result', smtps, resultReturned);
    return resultReturned;
}

async function fetchMRIs(smtps: string[]) {
    /* eslint-disable-next-line owa-custom-rules/telemetry-mailboxinfo-as-second-param, owa-custom-rules/telemetry-event-name-format -- (https://aka.ms/OWALintWiki)
     * BASELINE: DO NOT COPY/PASTE!
     * undefined
     *	> Argument at index 1 for logUsage must be of type MailboxInfo and not undefined. Telemetry calls that violate this rule will eventually be removed by the framework team. https://aka.ms/owa-analytics-mailboxinfo
     (https://aka.ms/OWALintWiki)
         * Baseline. Dev touching this file must update to pass the active MailboxInfo; Docs: https://aka.ms/owa-analytics-mailboxinfo
         * undefined
         *	> MailboxInfo must be passed into the function to identify the account.
         (https://aka.ms/OWALintWiki)
             * Baseline. DO NOT COPY AND PASTE! Grandfathering existing event names; Devs who touch this file should update Event Names to follow Outlook Telemetry Naming Standards
             *	> Invalid Outlook telemetry EventName "fetchMRIs": must start with a capital letter (A–Z). */
    logUsage('fetchMRIs', undefined, { batched: smtps.length });
    debugLog('fetchMRIs', smtps);
    if (shouldMockRequest()) {
        return mockMRIs(smtps);
    }
    return getMRIsFromGraphInBulk(smtps);
}
